<template>
  <div class="app-shell live">
    <header class="live__top">
      <img class="live__logo" src="/assets/logo.jpg" alt="" />
      <span class="tb-pill" :class="isOver ? 'tb-pill--over' : 'tb-pill--live'">
        <span v-if="!isOver" class="tb-pill__dot"></span>
        {{ statusLabel }}
      </span>
    </header>

    <section class="tb-card">
      <div class="called">
        <div class="called__meta">
          <span class="called__label">Last called</span>
          <span class="called__number text-funky">{{ lastNumber || "--" }}</span>
        </div>
        <span class="called__count">{{ polledNumbers.length }} <small>of 90</small></span>
      </div>
      <div class="tb-card__body">
        <NumberBoard :called="polledNumbers" :latest="lastNumber" />
      </div>
    </section>

    <section class="tb-card">
      <header class="tb-card__head">
        <p class="tb-card__title">Called so far</p>
      </header>
      <div class="tb-card__body">
        <div v-if="polledNumbers.length" class="called-list">
          <span
            v-for="(number, index) in polledNumbers"
            :key="index"
            class="tb-chip"
            :class="{ 'tb-chip--latest': index === polledNumbers.length - 1 }"
          >
            {{ number }}
          </span>
        </div>
        <p v-else class="tb-empty">Waiting for the first number.</p>
      </div>
    </section>

    <h2 class="tb-section-title">
      My tickets
      <span class="tb-pill">{{ myTickets.length }}</span>
    </h2>
    <div class="tb-stack">
      <TicketCard v-for="card in myTickets" :key="card.number" :card="card" :marked="polledNumbers" removable @remove="removeTicket" />
      <p v-if="!myTickets.length" class="tb-empty">Add your ticket to have its numbers marked off automatically.</p>
      <button type="button" class="tb-btn" @click="pickTicket">Select your ticket</button>
    </div>

    <h2 class="tb-section-title">Winners</h2>
    <div class="tb-stack">
      <section v-for="claim in declaredClaims" :key="claim.key" class="tb-card">
        <header class="tb-card__head">
          <p class="tb-card__title">{{ claim.name }}</p>
        </header>
        <div class="tb-card__body">
          <div class="tb-winner-list">
            <button v-for="winner in claim.users" :key="winner.ticket" type="button" class="tb-winner" @click="openWinner(winner)">
              <span class="tb-winner__ticket">Ticket {{ ticketNumber(winner.ticket) }}</span>
              <span class="tb-winner__name">🏅 {{ (winner.name || "").toUpperCase() }}</span>
            </button>
          </div>
        </div>
      </section>
      <p v-if="!declaredClaims.length" class="tb-empty">No claims won yet.</p>
    </div>

    <b-modal :active.sync="winnerModalActive" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title winner-modal__title">
            Ticket {{ activeWinnerNumber }}
            <small>{{ (activeWinner.name || "").toUpperCase() }}</small>
          </p>
        </header>
        <section class="modal-card-body winner-modal__body">
          <TicketGrid v-if="activeWinnerCard" :card="activeWinnerCard.card" :marked="winningNumbers" />
          <p class="winner-modal__foot">Won on number {{ activeWinner.at }}</p>
        </section>
      </div>
    </b-modal>

    <notifications group="live" />
  </div>
</template>

<script>
  import { mapState } from "vuex";
  import "firebase/firestore";
  import "firebase/database";
  import firebase from "firebase/app";
  import NumberBoard from "../components/NumberBoard.vue";
  import TicketCard from "../components/TicketCard.vue";
  import TicketGrid from "../components/TicketGrid.vue";
  import { WINNER_SOUND, ticketNumber, winnerLabel, winnerRank } from "../constants/game";

  export default {
    name: "Live",
    components: { NumberBoard, TicketCard, TicketGrid },
    data() {
      return {
        myTickets: [],
        polledNumbers: [],
        // One entry per enabled claim, ordered by prize value.
        claims: [],
        sheets: [],
        // Queue of clips waiting to be announced.
        notifier: [],
        winnerModalActive: false,
        activeWinner: {}
      };
    },
    computed: {
      ...mapState(["cards", "info", "currentGame"]),
      isOver() {
        return this.currentGame.status === "winner_declared";
      },
      statusLabel() {
        return this.isOver ? "Game over" : `Game is ${this.currentGame.status}`;
      },
      lastNumber() {
        return this.polledNumbers.length ? this.polledNumbers[this.polledNumbers.length - 1] : null;
      },
      declaredClaims() {
        return this.claims.filter((claim) => claim.users.length);
      },
      activeWinnerNumber() {
        return this.activeWinner.ticket ? ticketNumber(this.activeWinner.ticket) : "";
      },
      activeWinnerCard() {
        if (!this.activeWinnerNumber) return null;
        return this.cards.find((card) => card.number === this.activeWinnerNumber) || null;
      },
      // Only the numbers called up to the winning call, so the ticket shows the
      // board as it looked the moment the claim was made.
      winningNumbers() {
        return this.polledNumbers.slice(0, this.polledNumbers.indexOf(this.activeWinner.at) + 1);
      }
    },
    created() {
      // Detach callbacks collected here when the view goes away.
      this.teardown = [];
      this.announceTimer = null;

      this.subscribePolled();
      this.subscribeStatus();
      this.startAnnouncer();
      this.loadGame();
    },
    beforeDestroy() {
      clearInterval(this.announceTimer);
      this.teardown.forEach((off) => off());
      this.teardown = [];
    },
    methods: {
      ticketNumber,

      gameRef() {
        return firebase.database().ref("games").child(this.currentGame.uid);
      },

      subscribePolled() {
        const ref = this.gameRef().child("data").child("polled");
        const handler = ref.on("child_added", (snap) => {
          this.polledNumbers.push(snap.val());
          if (!this.isOver) {
            this.notifier.push(snap.val());
          }
        });
        this.teardown.push(() => ref.off("child_added", handler));
      },

      subscribeStatus() {
        const unsubscribe = firebase
          .firestore()
          .collection("games")
          .doc(this.currentGame.uid)
          .onSnapshot((doc) => {
            const data = doc.data();
            if (data && data.status === "winner_declared" && !this.isOver) {
              this.$buefy.dialog.alert({
                title: "Game Over!",
                ariaRole: "alertdialog",
                ariaModal: true
              });
              this.notifier.push("gamend");
              this.$store.commit("SET_GAME_STATUS", "winner_declared");
            }
          });
        this.teardown.push(unsubscribe);
      },

      async loadGame() {
        const snap = await this.gameRef().child("data").once("value");

        const sheetUsers = snap.child("sheetUsers").val();
        if (sheetUsers) {
          this.sheets = sheetUsers;
        }

        this.claims = Object.keys(snap.child("gameType").val() || {})
          .map((key) => ({ key, name: winnerLabel(key), users: [] }))
          .sort((a, b) => winnerRank(a.key) - winnerRank(b.key));

        this.subscribeWinners();
      },

      subscribeWinners() {
        const ref = this.gameRef().child("winners");
        const handler = ref.on("child_added", (snap) => {
          const claim = this.claims.find((item) => item.key === snap.key);
          if (!claim) return;
          snap.forEach((entry) => {
            claim.users.push(entry.val());
            if (!this.isOver) {
              this.notifier.push(WINNER_SOUND[snap.key]);
            }
          });
        });
        this.teardown.push(() => ref.off("child_added", handler));
      },

      // Numbers can arrive faster than they can be read out, so only the newest
      // pending clip is played and the rest of the queue is dropped.
      startAnnouncer() {
        this.announceTimer = setInterval(() => {
          if (!this.notifier.length) return;
          const item = this.notifier.pop();
          this.notifier = [];
          this.announce(item);
        }, 1000);
      },

      announce(item) {
        try {
          const audio = new Audio(require("@/assets/sound/" + item + ".mp3"));
          const playback = audio.play();
          // Browsers reject play() until the page has been interacted with.
          if (playback && playback.catch) {
            playback.catch(() => {});
          }
        } catch (error) {
          // No clip for this claim — fall back to the speech synthesiser.
          if (item > 90) this.say(item);
        }
      },

      say(message) {
        const utterance = new SpeechSynthesisUtterance();
        utterance.text = message;
        utterance.rate = 0.8;
        utterance.lang = "en-US";
        utterance.volume = 1;
        speechSynthesis.speak(utterance);
      },

      pickTicket() {
        this.$buefy.dialog.prompt({
          message: "Enter your ticket number",
          inputAttrs: { type: "number", maxlength: 3, min: 1 },
          trapFocus: true,
          onConfirm: (value) => this.addTicket(value)
        });
      },

      addTicket(value) {
        const number = parseInt(value, 10);
        if (!number) return;

        if (this.hasTicket(number)) {
          return this.notify(`Ticket ${ticketNumber(number)} is already on your list.`);
        }
        if (!this.cards[number - 1]) {
          return this.notify(`There is no ticket ${number} in this game.`);
        }

        // A ticket sold as part of a half/full sheet brings its siblings along.
        const sheet = this.sheets.find((tickets) => tickets.includes(number));
        if (sheet) {
          sheet.forEach((ticket) => {
            const sibling = this.cards[parseInt(ticket, 10) - 1];
            if (sibling && !this.hasTicket(ticket)) {
              this.myTickets.push(sibling);
            }
          });
          return;
        }

        this.myTickets.push(this.cards[number - 1]);
      },

      hasTicket(value) {
        const number = ticketNumber(value);
        return this.myTickets.some((card) => card.number === number);
      },

      removeTicket(card) {
        this.myTickets = this.myTickets.filter((ticket) => ticket.number !== card.number);
      },

      openWinner(winner) {
        this.activeWinner = winner;
        this.winnerModalActive = true;
      },

      notify(message) {
        this.$buefy.snackbar.open({
          message,
          type: "is-warning",
          queue: false,
          position: "is-top",
          actionText: null
        });
      }
    }
  };
</script>

<style scoped>
  .live__top {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 10px 0 16px;
  }

  .live__logo {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
  }

  /* Headline "last called" panel above the board. */
  .called {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    background: var(--tb-deep);
    color: #fff;
  }

  .called__meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
  }

  .called__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .called__count {
    flex: none;
    font-size: clamp(18px, 6vw, 24px);
    font-variant-numeric: tabular-nums;
  }

  .called__count small {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .called__number {
    font-size: clamp(30px, 11vw, 46px);
    line-height: 1;
    color: var(--tb-accent);
    font-variant-numeric: tabular-nums;
  }

  .called-list {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  .winner-modal__title {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    align-items: baseline;
    font-size: 15px;
  }

  .winner-modal__title small {
    font-size: 13px;
    color: var(--tb-ink-soft);
  }

  .winner-modal__body {
    background: var(--tb-ticket);
  }

  .winner-modal__foot {
    margin-top: 12px;
    color: #fff;
    font-size: 13px;
    text-align: center;
  }
</style>
